import { useRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowUp, ArrowDown, ImageIcon, X, Trash2, Wand2, ArrowRight,
  Loader2, Check, Circle, Upload, GripVertical, Plus, Layers,
} from "lucide-react";

/**
 * Derive a scene's status from its data + global generation state.
 */
const deriveStatus = (scene, reelBuilding, boardHasReel) => {
  if (reelBuilding) return "generating";
  if (boardHasReel && (scene.character_image_url || scene.uploaded_image_url)) return "complete";
  const hasFrame = !!(scene.character_image_url || scene.uploaded_image_url);
  const hasDesc = !!(scene.description || "").trim();
  if (hasFrame && hasDesc) return "ready";
  if (hasFrame || hasDesc || (scene.title || "").trim()) return "draft";
  return "idle";
};

const STATUS_STYLE = {
  idle:       { border: "border-[#F2C94C]/15", ring: "",                             pillBg: "bg-[#605C50]/15",  pillText: "text-white",     label: "IDLE",       Icon: Circle },
  draft:      { border: "border-[#F2C94C]/25", ring: "",                             pillBg: "bg-[#F2C94C]/15",  pillText: "text-[#F2C94C]",     label: "DRAFT",      Icon: Circle },
  ready:      { border: "border-[#00E5FF]/50", ring: "ring-1 ring-[#00E5FF]/30",     pillBg: "bg-[#00E5FF]/15",  pillText: "text-[#00E5FF]",     label: "READY",      Icon: Check  },
  generating: { border: "border-[#F2C94C]",    ring: "ring-2 ring-[#F2C94C]/60 animate-pulse", pillBg: "bg-[#F2C94C]", pillText: "text-[#0A0A0A]", label: "GENERATING", Icon: Loader2 },
  complete:   { border: "border-[#27AE60]",    ring: "ring-1 ring-[#27AE60]/40",     pillBg: "bg-[#27AE60]/20",  pillText: "text-[#27AE60]",     label: "COMPLETE",   Icon: Check  },
};

export const SceneNode = ({
  scene,
  index,
  totalScenes,
  reelBuilding,
  boardHasReel,
  onTitleChange,
  onDescriptionChange,
  onPickCharacter,
  onClearCharacter,
  onUploadImage,
  onClearUploaded,
  onAddOmni,
  onRemoveOmni,
  onDropRef,        // (destSceneIndex, url, target: "frame" | "omni") → attach a dragged ref URL
  onMoveUp,
  onMoveDown,
  onRemove,
  onSendToPipeline,
  dndId,
  // When another scene is being dragged and the pointer is currently over
  // THIS scene, `isDropTarget` is true — we render a gold-glow ring so the
  // user sees exactly which slot they'll trade with. Mirrors the attachment
  // rail's per-slot drop-target styling.
  isDropTarget = false,
}) => {
  const status = deriveStatus(scene, reelBuilding, boardHasReel);
  const s = STATUS_STYLE[status];
  const StatusIcon = s.Icon;
  const uploadRef = useRef(null);
  const omniRef = useRef(null);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: dndId ?? `scene-${index}` });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : "auto",
    opacity: isDragging ? 0.85 : 1,
  };

  const frameUrl = scene.uploaded_image_url || scene.character_image_url;
  const omniList = scene.omni_references || [];
  // Character Sheets carry up to 10 reference views (front, side, back, four
  // head studies, and close-ups). Manual uploads still respect a 4-cap via
  // parent handler; the display rail supports the full set from a sheet.
  const OMNI_CAP = 10;

  // ── HTML5 drag targets — enable dragging thumbs from this rail onto other
  // scenes so users can compose follow-up shots from a single reference sheet.
  const handleDragOver = (e) => {
    if (Array.from(e.dataTransfer.types || []).includes("application/x-geneo-ref")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    }
  };
  const handleDropOnFrame = (e) => {
    const url = e.dataTransfer.getData("application/x-geneo-ref");
    if (!url) return;
    e.preventDefault();
    const tag = e.dataTransfer.getData("application/x-geneo-tag") || "";
    onDropRef?.(index, url, "frame", tag);
  };
  const handleDropOnOmni = (e) => {
    const url = e.dataTransfer.getData("application/x-geneo-ref");
    if (!url) return;
    e.preventDefault();
    const tag = e.dataTransfer.getData("application/x-geneo-tag") || "";
    onDropRef?.(index, url, "omni", tag);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`scene-${index}`}
      data-scene-status={status}
      data-drop-target={isDropTarget ? "true" : "false"}
      className={`relative rounded-2xl bg-gradient-to-br from-[#120E15] via-[#120E15] to-[#0d1f24] border-2 ${
        isDropTarget && !isDragging
          // Gold-glow drop-target — matches the attachment rail pattern so
          // the swap gesture reads the same across the app.
          ? "border-[#F2C94C] ring-2 ring-[#F2C94C]/70 shadow-[0_0_40px_-4px_rgba(242,201,76,0.85)] scale-[1.015]"
          : `${s.border} ${s.ring}`
      } ${isDragging ? "shadow-[0_20px_60px_-10px_rgba(0,229,255,0.35)]" : ""} transition-all overflow-hidden`}
    >
      {/* Corner ornaments */}
      <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#F2C94C]/40 rounded-tl-2xl pointer-events-none" />
      <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#00E5FF]/40 rounded-tr-2xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#00E5FF]/40 rounded-bl-2xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#F2C94C]/40 rounded-br-2xl pointer-events-none" />

      {/* Generating shimmer overlay */}
      {status === "generating" && (
        <div className="shimmer-pass absolute inset-0 pointer-events-none" />
      )}

      {/* Header — drag handle + number + status pill + move controls */}
      <div className="relative flex items-center justify-between px-4 py-3 border-b border-[#F2C94C]/10 gap-2 flex-wrap">
        <div className="flex items-center gap-2.5">
          <button
            {...attributes}
            {...listeners}
            data-testid={`scene-drag-${index}`}
            className="w-7 h-9 rounded-lg border border-[#00E5FF]/25 text-white hover:text-[#00E5FF] hover:border-[#00E5FF] cursor-grab active:cursor-grabbing flex items-center justify-center touch-none"
            title="Drag to reorder"
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F2C94C] to-[#E27D60] text-[#0A0A0A] font-display font-bold text-sm flex items-center justify-center shadow-[0_0_16px_-4px_rgba(242,201,76,0.6)]">
            {index + 1}
          </div>
          <div className="flex flex-col">
            <div className="font-mono-hy text-[9px] text-white tracking-widest">SCENE · NODE</div>
            <div className={`font-mono-hy text-[10px] font-bold flex items-center gap-1.5 ${s.pillText}`}>
              <StatusIcon className={`w-3 h-3 ${status === "generating" ? "animate-spin" : ""}`} />
              {s.label}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onMoveUp(index)} disabled={index === 0}
            data-testid={`scene-move-up-${index}`}
            className="w-8 h-8 rounded-full border border-[#F2C94C]/20 text-white hover:text-[#F2C94C] hover:border-[#F2C94C] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
            title="Move up"
          ><ArrowUp className="w-3.5 h-3.5" /></button>
          <button
            onClick={() => onMoveDown(index)} disabled={index === totalScenes - 1}
            data-testid={`scene-move-down-${index}`}
            className="w-8 h-8 rounded-full border border-[#F2C94C]/20 text-white hover:text-[#F2C94C] hover:border-[#F2C94C] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
            title="Move down"
          ><ArrowDown className="w-3.5 h-3.5" /></button>
          <button
            onClick={() => onRemove(index)} disabled={totalScenes === 1}
            data-testid={`scene-delete-${index}`}
            className="w-8 h-8 rounded-full border border-[#E27D60]/25 text-[#E27D60] hover:bg-[#E27D60]/10 hover:border-[#E27D60] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
            title="Remove scene"
          ><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {/* Title bar — inline editable */}
      <div className="relative px-4 md:px-5 pt-4">
        <input
          value={scene.title || ""}
          onChange={(e) => onTitleChange(index, e.target.value)}
          placeholder={`Scene ${index + 1} · title`}
          data-testid={`scene-title-${index}`}
          className="w-full px-3 py-2 bg-transparent border-b border-[#F2C94C]/20 focus:border-[#F2C94C] focus:outline-none font-display text-lg md:text-xl text-white placeholder:text-white placeholder:font-body placeholder:text-base"
        />
      </div>

      {/* Body — character/uploaded frame + description */}
      <div className="relative p-4 md:p-5 grid md:grid-cols-[auto_1fr] gap-4 md:gap-5">
        {/* Frame slot — also a drop target for dragged reference thumbs */}
        <div
          onDragOver={handleDragOver}
          onDrop={handleDropOnFrame}
          className="w-40 h-40 md:w-44 md:h-44 rounded-2xl overflow-hidden border-2 border-[#00E5FF]/25 bg-[#0A0A0A] relative flex-shrink-0 group mx-auto md:mx-0"
          data-testid={`scene-frame-drop-${index}`}
        >
          {frameUrl ? (
            <>
              <img
                src={frameUrl}
                alt={`frame ${index}`}
                className="w-full h-full object-cover"
                data-testid={`scene-frame-${index}`}
              />
              {scene.uploaded_image_url ? (
                <button
                  onClick={() => onClearUploaded(index)}
                  data-testid={`scene-uploaded-clear-${index}`}
                  className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#0A0A0A]/85 text-[#E27D60] hover:bg-[#E27D60] hover:text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                  title="Clear uploaded image"
                >
                  <X className="w-3 h-3" />
                </button>
              ) : (
                <button
                  onClick={() => onClearCharacter(index)}
                  data-testid={`scene-char-clear-${index}`}
                  className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#0A0A0A]/85 text-[#E27D60] hover:bg-[#E27D60] hover:text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                  title="Clear character"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              {status === "complete" && (
                <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-transparent via-[#27AE60] to-transparent" />
              )}
              {/* Source label */}
              <div className="absolute bottom-1.5 left-1.5 font-mono-hy text-[8px] tracking-widest px-1.5 py-0.5 rounded-full bg-[#0A0A0A]/80 border border-[#F2C94C]/30 text-[#F2C94C]">
                {scene.uploaded_image_url ? "UPLOAD" : "CHARACTER"}
              </div>
            </>
          ) : (
            <div className="w-full h-full grid grid-rows-2 divide-y divide-[#00E5FF]/15">
              <button
                onClick={() => onPickCharacter(index)}
                data-testid={`scene-char-pick-${index}`}
                className="w-full flex flex-col items-center justify-center gap-1 text-[#00E5FF]/70 hover:text-[#00E5FF] hover:bg-[#00E5FF]/5 transition-all"
              >
                <ImageIcon className="w-5 h-5" />
                <span className="font-mono-hy text-[9px] tracking-widest">PICK CHARACTER</span>
              </button>
              <button
                onClick={() => uploadRef.current?.click()}
                data-testid={`scene-upload-btn-${index}`}
                className="w-full flex flex-col items-center justify-center gap-1 text-[#F2C94C]/70 hover:text-[#F2C94C] hover:bg-[#F2C94C]/5 transition-all"
              >
                <Upload className="w-5 h-5" />
                <span className="font-mono-hy text-[9px] tracking-widest">UPLOAD IMAGE</span>
              </button>
            </div>
          )}
          <input
            ref={uploadRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`scene-upload-input-${index}`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUploadImage(index, f);
              e.target.value = "";
            }}
          />
        </div>

        {/* Description + omni */}
        <div className="flex flex-col min-w-0 gap-3">
          <div>
            <label className="font-mono-hy text-[9px] text-white tracking-widest block mb-1.5">SCENE DESCRIPTION</label>
            <textarea
              value={scene.description}
              onChange={(e) => onDescriptionChange(index, e.target.value)}
              placeholder={`Describe what happens in scene ${index + 1} — setting, action, mood…`}
              rows={4}
              data-testid={`scene-desc-${index}`}
              className="w-full min-h-[110px] px-3 py-2.5 bg-[#0A0A0A] border border-[#F2C94C]/20 focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/40 focus:outline-none rounded-xl font-body text-sm text-white placeholder:text-white resize-none"
            />
          </div>

          {/* Reference Preview Rail — horizontally scrollable. Each thumb is
              draggable; drop onto any other scene's frame slot (replaces the
              character image) or another scene's rail (appends as omni ref). */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="font-mono-hy text-[9px] text-white tracking-widest flex items-center gap-1.5">
                <Layers className="w-3 h-3" /> REFERENCE RAIL · {omniList.length}/{OMNI_CAP}
                {omniList.length > 0 && (
                  <span className="text-[#00E5FF]/70 normal-case tracking-normal hidden md:inline">· drag any angle onto another scene</span>
                )}
              </label>
              {frameUrl && (
                <button
                  onClick={() => onSendToPipeline(scene)}
                  data-testid={`scene-to-pipeline-${index}`}
                  className="font-mono-hy text-[10px] px-3 py-1 rounded-full border border-[#F2C94C]/30 text-[#F2C94C] hover:bg-[#F2C94C]/10 hover:border-[#F2C94C] transition-all flex items-center gap-1.5"
                >
                  <Wand2 className="w-3 h-3" /> Send to Pipeline <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </div>
            <div
              onDragOver={handleDragOver}
              onDrop={handleDropOnOmni}
              className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory scrollbar-thin"
              style={{ scrollbarWidth: "thin" }}
              data-testid={`scene-omni-strip-${index}`}
            >
              {omniList.map((url, oi) => {
                const tag = scene.omni_reference_tags?.[oi] || "";
                return (
                  <div
                    key={`${url}-${oi}`}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = "copy";
                      e.dataTransfer.setData("application/x-geneo-ref", url);
                      if (tag) e.dataTransfer.setData("application/x-geneo-tag", tag);
                    }}
                    className="relative w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden border border-[#00E5FF]/30 group snap-start cursor-grab active:cursor-grabbing hover:border-[#00E5FF] hover:shadow-[0_0_14px_-4px_rgba(0,229,255,0.8)] transition-all"
                    data-testid={`scene-omni-item-${index}-${oi}`}
                    title={tag ? `${tag} — drag onto another scene to attach this angle` : "Drag onto another scene to attach this angle"}
                  >
                    <img src={url} alt={tag || `omni ${oi}`} className="w-full h-full object-cover pointer-events-none" data-testid={`scene-omni-${index}-${oi}`} />
                    {tag && (
                      <span
                        data-testid={`scene-omni-tag-${index}-${oi}`}
                        className="absolute bottom-0 inset-x-0 px-1 py-0.5 font-mono-hy text-[8px] text-[#00E5FF] tracking-widest text-center leading-tight pointer-events-none bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent truncate"
                      >
                        {tag.toUpperCase()}
                      </span>
                    )}
                    <button
                      onClick={() => onRemoveOmni(index, oi)}
                      data-testid={`scene-omni-remove-${index}-${oi}`}
                      className="absolute inset-0 bg-[#0A0A0A]/70 opacity-0 group-hover:opacity-100 flex items-center justify-center text-[#E27D60] transition-opacity"
                      title="Remove reference"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
              {omniList.length < OMNI_CAP && (
                <>
                  <button
                    onClick={() => omniRef.current?.click()}
                    data-testid={`scene-omni-add-${index}`}
                    className="w-14 h-14 flex-shrink-0 rounded-lg border-2 border-dashed border-[#00E5FF]/30 text-[#00E5FF]/70 hover:border-[#00E5FF] hover:text-[#00E5FF] hover:bg-[#00E5FF]/5 flex flex-col items-center justify-center gap-0.5 transition-all snap-start"
                    title="Add reference"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="font-mono-hy text-[7px] tracking-widest">REF</span>
                  </button>
                  <input
                    ref={omniRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid={`scene-omni-input-${index}`}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onAddOmni(index, f);
                      e.target.value = "";
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Vertical connector between two scene nodes — a stylized flow arrow with dual gold+cyan glow.
 */
export const NodeConnector = ({ fromIndex, active }) => (
  <div
    aria-hidden="true"
    data-testid={`scene-connector-${fromIndex}`}
    className="relative flex items-center justify-center py-2 md:py-3"
  >
    <div className="relative flex flex-col items-center">
      <div className={`w-0.5 h-6 md:h-8 bg-gradient-to-b from-[#F2C94C]/60 to-[#00E5FF]/60 ${active ? "shadow-[0_0_10px_rgba(242,201,76,0.6)]" : ""}`} />
      <div className="relative -mt-1">
        <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-[#00E5FF]" />
      </div>
      <div className="absolute -right-8 top-1/2 -translate-y-1/2 font-mono-hy text-[9px] text-white tracking-widest whitespace-nowrap">
        NEXT
      </div>
    </div>
  </div>
);
